import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import AsteriskSymbol from "../../../../reusableComponents/AsteriskSymbol";
import { editModeOn } from "../../../../utilFunctions";
import { functionalities, INVESTIGATION_TYPES } from "../../../../consts";
import LoadingPage from "../../../../reusableComponents/loader/loadingPage";
import Select from 'react-select';
import "../Investigation/investigation.scss";
import { getVendorsList } from "../../../vendorMaster/helper";
import { fetchSurveyorList } from "../../../bulkSurvey/bulkApiHelper";
import { requestInvestigation } from "../Investigation/investigationHelper";
import CloseMedicalReviewForm from "./CloseMedicalReviewForm";

const MedicalReviewRequest = (props) => {
  const {
    modalData,
    claimDetails,
    setNotified,
    requestButtonText,
    mode,
    onClose,
    medicalReviewList,
    roleDataCookie
  } = props;

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(true);
  const [doctorOptions, setDoctorOptions] = useState([]);

  const [reviewPayload, setReviewPayload] = useState({
    claimIntimationNumber: claimDetails?.claimId,
    agencyName: '',
    doctorName: '',
    reasonForMedicalReview: '',
    doctorMobile: '',
    createdBy: '',
  });

  useEffect(() => {
    const loadVendors = async () => {
      try {
        getVendorsList(setAgencyOptions, false, true);
      } catch (error) {
        toast.error("Failed to load agency list.");
      } finally {
        setLoadingAgencies(false);
      }
    };
    loadVendors();
  }, []);

  const getDoctorList = async (id) => {
    const doctors = await fetchSurveyorList({ id: id });
    setDoctorOptions(doctors);
  };

  const toggleRequestReview = () => {
    if (medicalReviewList?.every((medicalReview)=>{ return medicalReview?.investigationStatus.toLowerCase()  === 'closed'})) {
      setShowRequestForm(true);
    }
  }

  const submitMedicalReviewRequest = async () => {
    if (!reviewPayload.doctorName) {
      toast.error("Please select a Medical Reviewer Name.");
      return;
    }
    if (!reviewPayload.reasonForMedicalReview) {
      toast.error("Please enter the Reason for Medical Review.");
      return;
    }

    setLoadingRequest(true);
    try {
      const payload = {
        vendorId: agencyOptions.find(opt => opt.value === reviewPayload.agencyName)?.value,
        investigatorId: doctorOptions.find(opt => opt.value === reviewPayload.doctorName)?.id,
        reasonForInvestigation: reviewPayload.reasonForMedicalReview,
        mobileNo: reviewPayload.doctorMobile,
        investigationType: INVESTIGATION_TYPES.MEDICAL,
        investigationRequestedBy:roleDataCookie?.emailID
      };

      const response = await requestInvestigation(claimDetails?.claimId, payload);

      if (response) {
        toast.success("Medical review request submitted successfully!");
        setNotified(true);
        setShowRequestForm(false);
        setReviewPayload({
          ...reviewPayload,
          agencyName: "",
          doctorName: "",
          reasonForMedicalReview: "",
          doctorMobile: "",
        });
      } else {
        toast.error("Failed to submit medical review request.");
      }
    } catch (error) {
      toast.error("An error occurred while submitting the request.");
    } finally {
      setLoadingRequest(false);
    }
  };

  const clearData = () => {
    setShowRequestForm(false);
    setReviewPayload({
      ...reviewPayload,
      agencyName: "",
      doctorName: "",
      reasonForMedicalReview: "",
      doctorMobile: "",
    });
    if (onClose) onClose();
  };

  const renderFormContent = () => {
    if (mode === 'close') {
      return (
        <CloseMedicalReviewForm
          onClose={onClose}
          // updateClaimSubstatus={updateClaimSubstatus}
          claimDetails={claimDetails}
          modalData={modalData}
        />
      );
    }

    const isSubmitDisabled = !reviewPayload.doctorName || !reviewPayload.reasonForMedicalReview;

    return (
      <>
        <h4 className="form-heading">Request Medical Review</h4>
        <div className="form-group">
          <label className="form-label">Agency Name</label>
          {loadingAgencies ? (
            <div>Loading agencies...</div>
          ) : (
            <Select
              options={agencyOptions}
              value={agencyOptions.find(option => option.value === reviewPayload.agencyName)}
              onChange={(selectedOption) => {
                setReviewPayload({
                  ...reviewPayload,
                  agencyName: selectedOption?.value || "",
                  doctorName: "" // Clear doctor when agency changes
                });
                getDoctorList(selectedOption?.value || "");
              }}
              placeholder="Select Agency"
              isClearable
            />
          )}
        </div>
        <div className="form-group">
          <label className="form-label">
            Medical Reviewer Name <AsteriskSymbol />
          </label>
          <Select
            options={doctorOptions}
            value={doctorOptions.find(option => option.value === reviewPayload.doctorName)}
            onChange={(selectedOption) =>
              setReviewPayload({
                ...reviewPayload,
                doctorName: selectedOption?.value || "",
              })
            }
            placeholder="Select Medical Reviewer"
            isClearable
          />
        </div>
        <div className="form-group">
          <label className="form-label">Medical Reviewer Mobile Number</label>
          <input
            type="text"
            className="form-control"
            placeholder="Enter Mobile Number"
            value={reviewPayload.doctorMobile}
            maxLength={10}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/[^0-9]/g, '');
              setReviewPayload({
                ...reviewPayload,
                doctorMobile: numericValue,
              });
            }}
          />
        </div>
        <div className="form-group">
          <label className="form-label">
            Reason for Medical Review <AsteriskSymbol />
          </label>
          <textarea
            className="form-control"
            placeholder="Enter reason for medical review"
            value={reviewPayload.reasonForMedicalReview}
            maxLength={400}
            onChange={(e) =>
              setReviewPayload({
                ...reviewPayload,
                reasonForMedicalReview: e.target.value,
              })
            }
          />
          <div className="text-end small text-muted">
            {reviewPayload.reasonForMedicalReview.length}/400 characters
          </div>
        </div>
        <div className="d-flex justify-content-center gap-4">
          <button className="btn btn-outline-success" onClick={clearData}>
            Cancel
          </button>
          <button
            className="btn btn-success"
            onClick={submitMedicalReviewRequest}
            disabled={isSubmitDisabled}
          >
            Submit
          </button>
        </div>
      </>
    );
  };

  return (
    <>
      {editModeOn(functionalities.MEDICAL_REVIEW_TAB) && !mode && (
        <div
          onClick={toggleRequestReview}
          className={medicalReviewList?.every((medicalReview)=>{ return medicalReview?.investigationStatus.toLowerCase()  === 'closed'}) ? "requestCalc fw-bold" : "requestCalc fw-bold bg-secondary"}
        >
          {requestButtonText ? requestButtonText : "Request Medical Review"}
        </div>
      )}

      {(showRequestForm || mode) && (
        <div id="modal-overlay" className="text-start">
         {loadingRequest && <LoadingPage kshemaLoader />}
           {!loadingRequest && (
            <div className="field-survey-form">
              <div className="overlay-text-calc text-right me-3">
                <span
                  type="button"
                  onClick={clearData}
                  className="close"
                  aria-label="Close"
                />
              </div>
              <div className="form-content-wrapper">{renderFormContent()}</div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MedicalReviewRequest;
